import { useState, useMemo, useRef, useEffect } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { useScheduledPosts, useEditPost, useClearPending, useScheduleLimit } from '@/api/hooks/usePoster'
import { useAccounts } from '@/api/hooks/useAccounts'
import { useAuth } from '@/api/hooks/useAuth'
import { useYouTubeChannels } from '@/api/hooks/useYouTube'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { StatCard } from '@/components/shared/StatCard'
import { toast } from 'sonner'
import { Calendar, List, Plus, Clock, CheckCircle, AlertTriangle, Send, Rocket, XIcon, Save, Megaphone, Trash2, ChevronDown } from 'lucide-react'
import { CalendarView } from './CalendarView'
import { ListView } from './ListView'
import { CreatePostWizard } from './CreatePostWizard'
import { CampaignDialog } from './CampaignDialog'
import { ScheduleAlert } from '@/components/shared/ScheduleAlert'
import type { ScheduledPost } from '@/types'

export function Poster() {
  const { t, language } = useLanguage()
  const { data: posts, isLoading: postsLoading } = useScheduledPosts()
  const { data: accounts, isLoading: accountsLoading } = useAccounts()
  const { data: user } = useAuth()

  // Conecta as páginas no app dedicado de POSTAGEM (isolado do broadcast).
  // Sempre via Laravel (/oauth/* → Laravel). 1 autorização traz todas as páginas da conta.
  const connectPosting = () => {
    window.location.href = `/oauth/redirect?userId=${encodeURIComponent(user?.id || '')}&returnTo=${encodeURIComponent('/publicar')}&app=posting`
  }
  const { data: ytChannels } = useYouTubeChannels()
  const editPost = useEditPost()
  const clearPending = useClearPending()
  const { data: scheduleLimit } = useScheduleLimit()
  const [createOpen, setCreateOpen] = useState(false)
  const [campaignOpen, setCampaignOpen] = useState(false)
  const [editingPost, setEditingPost] = useState<ScheduledPost | null>(null)
  const [clearDropdownOpen, setClearDropdownOpen] = useState(false)
  const clearDropdownRef = useRef<HTMLDivElement>(null)
  const [expandedPage, setExpandedPage] = useState<string | null>(null)
  const [selectedTypes, setSelectedTypes] = useState<Record<string, boolean>>({ photo: true, clip: true, long_video: true, story: true })

  const getPostType = (p: ScheduledPost): string => {
    if (p.postType === 'story') return 'story'
    if (p.mediaType === 'video' && p.campaignId) return 'clip'
    if (p.mediaType === 'video') return 'long_video'
    return 'photo'
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clearDropdownRef.current && !clearDropdownRef.current.contains(e.target as Node)) {
        setClearDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Edit form state
  const [editMessage, setEditMessage] = useState('')
  const [editImageUrl, setEditImageUrl] = useState('')
  const [editScheduleAt, setEditScheduleAt] = useState('')
  const [editCategory, setEditCategory] = useState('')

  const stats = useMemo(() => {
    if (!posts) return { pending: 0, published: 0, failed: 0, total: 0 }
    return {
      total: posts.length,
      pending: posts.filter(p => !p.published).length,
      published: posts.filter(p => p.status === 'published' || (p.published && p.status !== 'failed')).length,
      failed: posts.filter(p => p.status === 'failed').length,
    }
  }, [posts])

  const pagesWithPending = useMemo(() => {
    if (!posts) return []
    const pending = posts.filter(p => !p.published && p.status !== 'published' && p.status !== 'failed')
    const map = new Map<string, { pageId: string; name: string; count: number; byType: Record<string, number> }>()
    for (const p of pending) {
      const key = p.pageId
      if (!map.has(key)) {
        map.set(key, { pageId: key, name: p.accountName || key, count: 0, byType: { photo: 0, clip: 0, long_video: 0, story: 0 } })
      }
      const entry = map.get(key)!
      entry.count++
      const type = getPostType(p)
      entry.byType[type] = (entry.byType[type] || 0) + 1
    }
    return Array.from(map.values())
  }, [posts])

  const openEdit = (post: ScheduledPost) => {
    setEditingPost(post)
    setEditMessage(post.message)
    setEditImageUrl(post.imageUrl || '')
    setEditCategory(post.category || '')
    // Convert timestamp to datetime-local format
    const d = new Date(post.scheduledAt)
    const offset = d.getTimezoneOffset()
    const local = new Date(d.getTime() - offset * 60000)
    setEditScheduleAt(local.toISOString().slice(0, 16))
  }

  const handleEditSave = () => {
    if (!editingPost) return
    if (!editMessage.trim()) { toast.error(t('Digite a mensagem')); return }
    editPost.mutate({
      postId: editingPost.id,
      message: editMessage.trim(),
      imageUrl: editImageUrl.trim() || undefined,
      scheduleAt: editScheduleAt || undefined,
      category: editCategory,
    }, {
      onSuccess: () => {
        toast.success(t('Post atualizado!'))
        setEditingPost(null)
      },
      onError: () => toast.error(t('Erro ao atualizar')),
    })
  }

  if (postsLoading || accountsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-40 rounded-xl" />
        <div className="grid gap-4 sm:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
            <Rocket className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">{t('Publicar')}</h1>
            <p className="text-xs text-muted-foreground">{t('Gerencie e agende publicacoes para varias paginas')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stats.pending > 0 && (
            <div className="relative" ref={clearDropdownRef}>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs rounded-xl border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
                disabled={clearPending.isPending}
                onClick={() => {
                  setClearDropdownOpen(!clearDropdownOpen)
                  if (!clearDropdownOpen) {
                    setExpandedPage(null)
                    setSelectedTypes({ photo: true, clip: true, long_video: true, story: true })
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {clearPending.isPending ? t('Limpando...') : t('Limpar Agendados')}
                <ChevronDown className="h-3 w-3" />
              </Button>
              {clearDropdownOpen && (
                <div className="absolute right-0 top-full z-50 mt-1.5 min-w-[260px] rounded-xl border border-border/40 bg-popover/95 backdrop-blur-md p-1.5 shadow-xl slide-up">
                  {pagesWithPending.map(page => (
                    <div key={page.pageId}>
                      <button
                        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs hover:bg-muted/60 transition-colors"
                        onClick={() => setExpandedPage(expandedPage === page.pageId ? null : page.pageId)}
                      >
                        <span className="truncate font-medium">{page.name}</span>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="secondary" className="text-[11px] rounded-md">{page.count}</Badge>
                          <ChevronDown className={`h-3 w-3 transition-transform ${expandedPage === page.pageId ? 'rotate-180' : ''}`} />
                        </div>
                      </button>
                      {expandedPage === page.pageId && (
                        <div className="px-3 pb-2 space-y-1.5">
                          {([
                            { key: 'photo', label: 'Fotos' },
                            { key: 'clip', label: 'Cortes' },
                            { key: 'long_video', label: 'Videos Longos' },
                            { key: 'story', label: 'Stories' },
                          ] as const).filter(t => page.byType[t.key] > 0).map(tp => (
                            <label key={tp.key} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                              <input
                                type="checkbox"
                                checked={selectedTypes[tp.key]}
                                onChange={() => setSelectedTypes(prev => ({ ...prev, [tp.key]: !prev[tp.key] }))}
                                className="h-3.5 w-3.5 rounded border-border/60 accent-primary"
                              />
                              <span className="flex-1">{tp.label}</span>
                              <span className="text-muted-foreground tabular-nums">({page.byType[tp.key]})</span>
                            </label>
                          ))}
                          <button
                            className="w-full mt-1.5 rounded-lg bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-40"
                            disabled={!Object.values(selectedTypes).some(Boolean) || clearPending.isPending}
                            onClick={() => {
                              const active = Object.entries(selectedTypes).filter(([, v]) => v).map(([k]) => k)
                              const activeCount = active.reduce((sum, k) => sum + (page.byType[k] || 0), 0)
                              if (!confirm(`Limpar ${activeCount} agendamento(s) de ${page.name}?`)) return
                              setClearDropdownOpen(false)
                              setExpandedPage(null)
                              clearPending.mutate({ pageId: page.pageId, types: active }, {
                                onSuccess: (data) => toast.success(`${data.removed} programacao(oes) removida(s)`),
                                onError: () => toast.error(t('Erro ao limpar programacoes')),
                              })
                            }}
                          >
                            {clearPending.isPending ? 'Limpando...' : 'Limpar Selecionados'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col items-center gap-1">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs rounded-xl" onClick={connectPosting} title="Conecta as páginas no app dedicado de postagem (isolado do broadcast)">
              <Plus className="h-3.5 w-3.5" />
              Conectar postagem
            </Button>
            <span className="text-[11px] text-muted-foreground text-center leading-tight max-w-[160px]">App próprio de postagem (isolado do broadcast)</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs rounded-xl" onClick={() => setCampaignOpen(true)}>
              <Megaphone className="h-3.5 w-3.5" />
              {t('Campanha')}
            </Button>
            <span className="text-[11px] text-muted-foreground text-center leading-tight max-w-[160px]">Imagens, stories e cortes para FB e IG</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Button size="sm" className="gap-1.5 text-xs rounded-xl" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              {t('Programar Videos Longos')}
            </Button>
            <span className="text-[11px] text-muted-foreground text-center leading-tight max-w-[180px]">Videos longos para YouTube, Reels IG e FB</span>
          </div>
        </div>
      </div>

      {language === 'en' && (
        <div className="flex items-start gap-2.5 rounded-xl border border-blue-500/20 bg-blue-500/5 px-4 py-2.5">
          <span className="mt-0.5 text-blue-500">&#8505;</span>
          <p className="text-xs text-muted-foreground">Manages scheduled content publishing to authorized Facebook Pages and Instagram accounts via the Content Publishing API.</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label={t('Total de posts')} value={stats.total} icon={Send} color="blue" />
        <StatCard label={t('Agendados')} value={stats.pending} icon={Clock} color="yellow" />
        <StatCard label={t('Publicados')} value={stats.published} icon={CheckCircle} color="green" />
        <StatCard label={t('Com falha')} value={stats.failed} icon={AlertTriangle} color="red" />
      </div>

      {/* Schedule limit bar */}
      {scheduleLimit && !scheduleLimit.unlimited && (() => {
        const pct = scheduleLimit.limit ? (scheduleLimit.current / scheduleLimit.limit) * 100 : 0
        const isLow = scheduleLimit.remaining !== null && scheduleLimit.remaining < 100
        return (
          <div className={`relative overflow-hidden rounded-xl border px-4 py-3 ${
            isLow
              ? 'border-red-500/30 bg-red-500/5'
              : 'border-yellow-500/20 bg-yellow-500/5'
          }`}>
            <div className="flex items-center gap-2.5">
              <Clock className={`h-4 w-4 shrink-0 ${isLow ? 'text-red-500' : 'text-yellow-500'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-xs font-semibold">
                    Agendamentos: <strong className="tabular-nums">{scheduleLimit.current}</strong> / {scheduleLimit.limit}
                  </span>
                  {scheduleLimit.remaining !== null && (
                    <span className={`text-[11px] font-medium ${isLow ? 'text-red-400' : 'text-yellow-500'}`}>
                      {scheduleLimit.remaining} restantes
                    </span>
                  )}
                </div>
                <div className="h-1.5 w-full rounded-full bg-muted/60 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${isLow ? 'bg-red-500' : 'bg-yellow-500'}`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Schedule alert – days remaining per page */}
      <ScheduleAlert />

      {/* Tabs */}
      <Tabs defaultValue="calendar">
        <div className="mb-4">
          <TabsList className="inline-flex h-11 items-center rounded-2xl bg-muted/60 p-1 gap-1 backdrop-blur-sm">
            <TabsTrigger
              value="calendar"
              className="rounded-xl px-4 py-2 text-xs font-medium transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <Calendar className="mr-1.5 h-3.5 w-3.5" /> {t('Calendario')}
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="rounded-xl px-4 py-2 text-xs font-medium transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              <List className="mr-1.5 h-3.5 w-3.5" /> {t('Lista')}
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="calendar" className="m-0">
          <CalendarView posts={posts || []} accounts={accounts || []} ytChannels={ytChannels || []} onEdit={openEdit} />
        </TabsContent>
        <TabsContent value="list" className="m-0">
          <ListView posts={posts || []} accounts={accounts || []} ytChannels={ytChannels || []} onEdit={openEdit} />
        </TabsContent>
      </Tabs>

      {/* Create dialog */}
      <CreatePostWizard
        open={createOpen}
        onOpenChange={setCreateOpen}
        accounts={accounts || []}
      />

      {/* Campaign dialog */}
      <CampaignDialog
        open={campaignOpen}
        onOpenChange={setCampaignOpen}
        accounts={accounts || []}
      />

      {/* Edit dialog */}
      <Dialog open={!!editingPost} onOpenChange={(open) => { if (!open) setEditingPost(null) }}>
        <DialogContent
          className="max-w-md rounded-2xl border-border/40 p-0"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {editingPost && (
            <>
              <div className="flex items-center justify-between border-b border-border/40 px-5 py-4">
                <span className="text-sm font-bold">{t('Editar Post')}</span>
                <DialogClose className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground">
                  <XIcon className="h-4 w-4" />
                </DialogClose>
              </div>
              <div className="space-y-4 px-5 pb-5 pt-1">
                <div>
                  <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Mensagem')}</Label>
                  <Textarea
                    value={editMessage}
                    onChange={(e) => setEditMessage(e.target.value)}
                    className="min-h-[80px] resize-none text-sm rounded-xl border-border/40"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('URL da imagem')}</Label>
                  <Input
                    value={editImageUrl}
                    onChange={(e) => setEditImageUrl(e.target.value)}
                    placeholder="https://..."
                    className="h-9 text-xs rounded-xl border-border/40"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Categoria')}</Label>
                  <Input
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    placeholder="Ex: Promocao"
                    className="h-9 text-xs rounded-xl border-border/40"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('Agendar para')}</Label>
                  <Input
                    type="datetime-local"
                    value={editScheduleAt}
                    onChange={(e) => setEditScheduleAt(e.target.value)}
                    className="h-9 text-xs rounded-xl border-border/40"
                  />
                </div>
                <Button
                  onClick={handleEditSave}
                  disabled={editPost.isPending}
                  className="w-full gap-2 rounded-xl"
                >
                  <Save className="h-4 w-4" />
                  {editPost.isPending ? t('Salvando...') : t('Salvar Alteracoes')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
